export type TimeZoneSource = 'browser' | 'ip' | 'utc'

export const getBrowserTimeZone = (): string | null => {
  if (typeof Intl === 'undefined') return null
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
  return resolved || null
}

const toDate = (value: string | number | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

export const formatInTimeZone = (
  value: string | number | Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
  fallback = 'N/A'
) => {
  const date = toDate(value)
  if (!date) return fallback
  try {
    return new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(
      date
    )
  } catch (error) {
    console.error('Failed to format in time zone:', error)
    return fallback
  }
}

export const formatDateTimeLong = (
  value: string | number | Date,
  timeZone: string,
  fallback = 'N/A'
) => {
  const date = toDate(value)
  if (!date) return fallback

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })

    const parts = formatter.formatToParts(date)
    const getPart = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find(part => part.type === type)?.value || ''

    const weekday = getPart('weekday')
    const month = getPart('month')
    const day = getPart('day')
    const year = getPart('year')
    const hour = getPart('hour')
    const minute = getPart('minute')
    const dayPeriod = getPart('dayPeriod')

    const suffix = dayPeriod ? ` ${dayPeriod}` : ''
    return `${weekday}, ${month} ${day}, ${year}, ${hour}:${minute}${suffix}`
  } catch (error) {
    console.error('Failed to format long date time:', error)
    return fallback
  }
}

export const toUtcISOStringFromLocalDate = (value: string) => {
  if (!value) return value
  const date = new Date(`${value}T00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString()
}

export const toUtcISOStringFromLocalDateTime = (value: string) => {
  if (!value) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString()
}
